import type * as React from "react";
import { Box } from "../../components/ui/box";
import { Button } from "../../components/ui/button";
import { Icon } from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import type { Omittable } from "../../lib/props";

export type EntitySelectorStepHeaderProps = Omittable<{
  /** The step's name — "Create Field". */
  title: string;
  /** Back to the list the step was opened from. */
  onBack: () => void;
  ref?: React.Ref<HTMLDivElement>;
}>;

/**
 * The title row of a step a selector drilled into: a bare chevron back on the
 * left, the step's name beside it, on the list's 32px rung above the search
 * well. A picker whose footer verb needs one more answer — "Create Field"
 * asks WHICH kind — swaps its list for the step's list in the same popup
 * instead of opening a second surface, and this row is how the reader knows
 * where they are and how they get back. In the drawer it pins with the search
 * well above the scroller (see `EntitySelectorContent`).
 *
 * The chevron is the row's only control and sits on the popup's 6px edge, so
 * its wash lines up with the search well's frame; the title stays a title,
 * not a second press target.
 */
function EntitySelectorStepHeader({
  title,
  onBack,
  ref,
}: EntitySelectorStepHeaderProps) {
  return (
    <Box
      ref={ref}
      direction="row"
      align="center"
      gap="xs"
      height={32}
      shrink={false}
      // The search well's own 6px inset, minus nothing: the chevron's 24px
      // box starts where the well's frame starts, and the well below keeps
      // its top inset, so the two read as one pinned band.
      className="px-1.5 pt-1.5"
      data-slot="entity-selector-step-header"
    >
      <Button
        aria-label="Back"
        onClick={onBack}
        shape="square"
        size="icon-xs"
        variant="ghost"
      >
        <Icon name="chevron-left" size="sm" />
      </Button>
      <Text size="sm" weight="medium" truncate>
        {title}
      </Text>
    </Box>
  );
}

EntitySelectorStepHeader.displayName = "EntitySelectorStepHeader";

export { EntitySelectorStepHeader };
